<template>
  <WorkPackageSubPageModule
    :workpackage="workpackage"
    :isMobile="isMobile"
    :location="location"
    :loading="loading"
    :background_color="workpackage_bg_hex"
    :foreground_color="workpackage_fg_hex"
    header_icon="time-effort"
    header_text_short="TIME EFFORT"
    header_text_long="TIME EFFORT"
    @headerBack="$router.push({name: `${location}WorkPackageInfo`, params: { nav_back: $route.params.nav_back} })"
  >
    <template #body>
      <template v-if="takt_time_days.length > 0">
        <!-- ⌀∑ -->
        <div class="work-package-time-record-container">
          <div
            v-for="(item, i) in aggregated_header_values"
            :key="`TimeRecordHeadItem#${i}`"
            :style="`
              width: ${(100 / aggregated_header_values.length) || 0}%;
              border-right: ${i != aggregated_header_values.length - 1 ? `solid 1px ${getTranspHexColor(workpackage_fg_hex, 0.33)}` : ''};
            `"
          >
            <div class="work-package-time-record-aggregated-header-item">
              <TText
                :value="item.column"
                small
                :color="workpackage_fg_hex"
              />
              <div class="work-package-time-record-aggregated-header-item-column-value">
                <TText
                  :text="item.value.toString()"
                  :color="workpackage_fg_hex"
                />
                <TText
                  :text="`/${item.projected.toString()}`"
                  :color="workpackage_fg_hex"
                  line_height="1.5"
                  size="8"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="work-package-time-record-line" :style="`border-color: ${workpackage_fg_hex}`" />

        <RadioButtonTextGroup
          :id="`work-package-time-record-tabs`"
          :elements="[
            { text: 'On Site', value: 'on_site', },
            { text: 'Plan', value: 'plan', },
          ]"
          :selected="tab_selection"
          :color="workpackage_fg_hex"
          :font_weight="300"
          full_width
          @buttonClicked="tab_selection=$event"
        />

        <!-- ON SITE TAB -->
        <template v-if="tab_selection === 'on_site'">
          <!-- Eingabemaske -->
          <!-- Time Effort / Manpower = Duration -->
          <ModularEquation
            :no_space="true"
            :elements="on_site_elements"
            :background_color="workpackage_bg_hex"
            :foreground_color="workpackage_fg_hex"
            @changeTimeEffort="updateInputMask({time_effort: +$event})"
            @changeManpower="updateInputMask({manpower: +$event})"
            @changeDuration="updateInputMask({duration: +$event})"
          />

          <!-- Takt Time, Date, Save -->
          <div class="work-package-time-record-input-bottom-outer">
            <div class="work-package-time-record-input-bottom-inner">
              <DropDownOutlinedFlat
                label="Takt Time"
                :value="selected_day"
                :options="takt_time_days"
                width="75"
                :background_color="workpackage_bg_hex"
                :foreground_color="workpackage_fg_hex"
                @change="selected_day = {
                  value: $event,
                  text: $event,
                  takt_date: takt_time_days.find(day => day.value === $event).takt_date
                }"
              />
              <!-- Date -->
              <div>
                <TText :text="dayAndMonth" small :color="workpackage_fg_hex" />
                <TText :text="year" small :color="workpackage_fg_hex" />
              </div>
            </div>
            <ButtonOutlined
              icon="floppy-disc"
              value="Save"
              width="130"
              height="42"
              :color="workpackage_fg_hex"
              @buttonClicked="saveTimeRecord()"
            />
          </div>

          <div class="work-package-time-record-line" :style="`border-color: ${workpackage_fg_hex}`" />

          <!-- Time Record Entries -->
          <ClickableListItem
            v-for="data in reports"
            style="flex-shrink: 0;"
            :key="`dailyreport_id_${data.id}`"
            :id="data.id"
            :margin="2"
            :is_unclickable="true"
            padding="4px 0"
            :background_color="workpackage_bg_hex"
            :foreground_color="workpackage_fg_hex"
          >
            <template #front>
              <RadioButtonSquare
                :is_active="true"
                :background_color="workpackage_bg_hex"
                :foreground_color="workpackage_fg_hex"
                width="56"
              >
                <template #content>
                  <TText :text="getWeekDay(data.date)" :color="workpackage_bg_hex" />
                </template>
              </RadioButtonSquare>
            </template>

            <template #descriptor>
              <div class="work-package-time-record-list-descriptor">
                <TText
                  :text="data.date.split(' ')[0]"
                  :color="workpackage_fg_hex"
                  :size="8"
                  :line_height="1.4"
                  align="left"
                  font_weight="400"
                />
                <TText
                  value="Time Effort{value}"
                  :value_props="{value: ': ' + data.duration * data.manpower}"
                  :color="workpackage_fg_hex"
                  :size="8"
                  :line_height="1.4"
                  align="left"
                />
                <TText
                  value="Manpower{value}"
                  :value_props="{value: ': ' + data.manpower}"
                  :color="workpackage_fg_hex"
                  :size="8"
                  :line_height="1.4"
                  align="left"
                />
                <TText
                  value="Duration{value}"
                  :value_props="{value: ': ' + data.duration}"
                  :color="workpackage_fg_hex"
                  :size="8"
                  :line_height="1.4"
                  align="left"
                />
              </div>
            </template>

            <template #info>
              <!-- TODO: check for permission -->
              <button
                class="work-package-time-record-delete-button"
                v-ripple
                @click.prevent.stop="deleteTimeRecord(data.id)"
              >
                <TIcon
                  name="bin"
                />
              </button>
            </template>
          </ClickableListItem>
        </template>

        <!-- PLAN TAB -->
        <template v-if="tab_selection === 'plan'">
          <TText value="Time Effort Source" :color="workpackage_fg_hex" />
          <div class="work-package-time-record-plan-container">
            <div class="work-package-time-record-plan-source-container">
              <!-- WORK STEP SOURCE -->
              <div class="work-package-time-record-plan-source-inner-container">
                <div class="work-package-time-record-plan-source-header">
                  <RadioButtonSquare
                    :is_active="workpackage.time_effort_source === 'time_effort_steps'"
                    :background_color="workpackage_bg_hex"
                    :foreground_color="workpackage_fg_hex"
                    width="24"
                    @setActive="setPlanSource('time_effort_steps')"
                  >
                    <template #content>
                      <TIcon
                        v-if="workpackage.time_effort_source === 'time_effort_steps'"
                        name="check"
                      />
                    </template>
                  </RadioButtonSquare>
                  <TText value="WORK.steps" :color="workpackage_fg_hex" small />
                </div>
                <ModularEquation
                  :elements="[{
                    label: 'WORK.steps',
                    id: 'WORK.steps',
                    value: time_effort_steps,
                    is_disabled: true,
                    input_type: 'number',
                    unit: 'Mh' }]"
                  :background_color="workpackage_bg_hex"
                  :foreground_color="workpackage_fg_hex"
                />
              </div>

              <!-- WORK PACKAGE SOURCE -->
              <div class="work-package-time-record-plan-source-inner-container">
                <div class="work-package-time-record-plan-source-header">
                  <RadioButtonSquare
                    :is_active="workpackage.time_effort_source === 'time_effort_planned'"
                    :background_color="workpackage_bg_hex"
                    :foreground_color="workpackage_fg_hex"
                    width="24"
                    @setActive="setPlanSource('time_effort_planned')"
                  >
                    <template #content>
                      <TIcon
                        v-if="workpackage.time_effort_source === 'time_effort_planned'"
                        name="check"
                      />
                    </template>
                  </RadioButtonSquare>
                  <TText value="WORK.package" :color="workpackage_fg_hex" small />
                </div>
                <ModularEquation
                  :elements="[{
                    label: 'WORK.package',
                    id: 'WORK.package',
                    value: workpackage.time_effort_planned,
                    is_disabled: false,
                    input_type: 'number',
                    unit: 'Mh'
                  }]"
                  :background_color="workpackage_bg_hex"
                  :foreground_color="workpackage_fg_hex"
                  @changeWORKpackage="updateTimeEffort(+$event)"
                />
              </div>
            </div>

            <div id="work-package-time-record-svg-container" />

            <!-- Time Effort / Manpower = Duration -->
            <ModularEquation
              :elements="plan_elements"
              :background_color="workpackage_bg_hex"
              :foreground_color="workpackage_fg_hex"
              @changeManpower="updateManpower(+$event)"
            />
          </div>
        </template>
      </template>
      <template v-else>
        <div class="work-package-time-record-line" :style="`border-color: ${workpackage_fg_hex}`" />

        <div class="work-package-time-record-no-calendar-container">
          <TIcon name="exclamation-triangle" />
          <TText value="This project has no calendar" align="center" />
        </div>

        <TText value="Add a calendar in the Takt Plan to use the Time Effort tab" align="center" />
        <TText value="Make sure the edit mode is activated" align="center" />
      </template>
    </template>
  </WorkPackageSubPageModule>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import { WorkPackageReworkMixin } from '@/mixins/WorkPackageRework.mixin.js';
import { TColor } from '@/mixins/TColor.mixin.js';

import WorkPackageSubPageModule from '@/modules/WorkPackageWIP/WorkPackageSubPageModule.module.vue';
import ModularEquation from '@/modules/WorkPackageWIP/ModularEquation.module.vue';
import DropDownOutlinedFlat from '@/components/WorkPackageWIP/DropDownOutlinedFlat.component.vue';
import ButtonOutlined from '@/components/WorkPackageWIP/ButtonOutlined.component.vue';
import ClickableListItem from '@/components/WorkPackageWIP/ClickableListItem.component.vue';
import RadioButtonSquare from '@/components/WorkPackageWIP/RadioButtonSquare.component.vue';
import TIcon from '@/components/Taktify/TIcon.component.vue';
import RadioButtonTextGroup from '@/components/WorkPackageWIP/RadioButtonTextGroup.component.vue'

  export default {
    components: {
      WorkPackageSubPageModule,
      ModularEquation,
      DropDownOutlinedFlat,
      ButtonOutlined,
      ClickableListItem,
      RadioButtonSquare,
      TIcon,
      RadioButtonTextGroup,
    },

    mixins: [
      WorkPackageReworkMixin,
      TColor,
    ],

    props: [
      'wallet',
      'location',
      'prefix',
      'backlog',
    ],

    data: () => ({
      selected_day: {
        value: 'Mo',
        text: 'Mo',
        takt_date: ''
      },
      tab_selection: 'on_site',
      plan_source: '',
      on_site_elements: [
        // { label: 'Takt Time', value: 'Mo', is_disabled: false, width: 70, select: {options: [{value: 'Mo', text: 'Mo'}, {value: 'Tue', text: 'Tue'}]} },
        { label: 'Time Effort', id: 'Time Effort', value: '0', is_disabled: false, input_type: 'number', unit: 'Mh' },
        { operator: '/' },
        { label: 'Manpower', id: 'Manpower', value: '0', is_disabled: false, input_type: 'number', unit: 'M' },
        { operator: '=' },
        { label: 'Duration', id: 'Duration', value: '0', is_disabled: false, input_type: 'number', unit: 'h' },
      ],
      input_mask: {
        time_effort: 0,
        manpower: 0,
        duration: 0,
      },
    }),

    created() {
      this.loadData();

      let today = this.getWeekDay(new Date().toISOString().substring(0,10));

      if(this.takt_time_days.length > 0) {
        this.selected_day = {
          value: today,
          text: today,
          takt_date: this.takt_time_days.find(day => day.value === today).takt_date,
        };
      }
    },

    watch: {
      workpackage_id() {
        this.loadData();
      },

      tab_selection() {
        if(this.tab_selection === 'plan') {
          this.$nextTick(() => {
            this.drawSourceLines();
          })
        }
      },

      'workpackage.time_effort_source'() {
        this.drawSourceLines();
      },
    },

    computed: {
      ...mapGetters('Ultra', [
        'getUObjectsFiltered',
        'getUObjects',
      ]),

      ...mapGetters('Project', [
        'hasProjectPermission',
      ]),

      ...mapGetters('ProjectCalendar', [
        'getCalendar',
        'getWeekDaysSorted',
        'hasCalendar',
      ]),

      plan_elements() {
        let time_effort = 0;
        let manpower = this.workpackage.manpower_planned;

        if(this.workpackage.time_effort_source === 'time_effort_steps') {
          time_effort = this.workpackage.time_effort_steps;
        }
        else if(this.workpackage.time_effort_source === 'time_effort_planned') {
          time_effort = this.workpackage.time_effort_planned;
        }

        return [
          { label: 'Time Effort', id: 'Time Effort', value: time_effort, is_disabled: true, input_type: 'number', unit: 'Mh' },
          { operator: '/' },
          { label: 'Manpower', id: 'Manpower', value: manpower, is_disabled: false, input_type: 'number', unit: 'M' },
          { operator: '=' },
          { label: 'Duration', id: 'Duration', value: Math.round(time_effort / manpower * 100) / 100, is_disabled: true, input_type: 'number', unit: 'h' },
        ];
      },

      time_effort_steps() {
        let time_effort_steps = 0;

        if(this.workpackage[`${this.prefix}worksteps`].length === 0) {
          time_effort_steps = this.workpackage.time_effort_steps;
        }
        else {
          time_effort_steps = this.calcAllWorkStepsTotal({ workpackage: this.workpackage, prefix: `${this.histprefix}${this.prefix}` }).value;
        }

        return time_effort_steps;
      },

      reports() {
        let reports = this.getUObjects({type: 'dailyreports', ids: this.workpackage.dailyreports});

        reports.sort(function(a, b) {
          return (a.date < b.date) ? 1 : ((a.date > b.date) ? -1 : 0);
        });

        return reports;
      },

      dayAndMonth() {
        let date_str = this.takt_time_days.find(day => day.value === this.selected_day.value).takt_date.split("-");
        let ret = '';

        ret += +date_str[2]; // day without leading 0
        ret += '. '
        ret += this.$store.state.Ultra.months[+date_str[1] - 1]; // month by name

        return ret;
      },

      year() {
        let date_str = this.takt_time_days.find(day => day.value === this.selected_day.value).takt_date.split("-");
        return date_str[0];
      },

      aggregated_header_values() {
        let header_values = [];
        let dailyreports = this.getUObjects({type: 'dailyreports', ids: this.workpackage.dailyreports});

        let time_effort = 0;
        let time_effort_projected = this.plan_elements[0].value; // NOTE: this is the time effort value of modular equation array of objects
        dailyreports.forEach(el => time_effort += (el.manpower * el.duration));

        let manpower = 0;
        let manpower_projected = this.workpackage.manpower_planned;
        dailyreports.forEach(el => manpower += el.manpower);
        manpower /= dailyreports.length;
        manpower = Math.round(manpower * 100) / 100
        manpower_projected = Math.round(manpower_projected * 100) / 100

        let duration = 0;
        let duration_projected = time_effort_projected / manpower_projected;
        dailyreports.forEach(el => duration += el.duration);
        duration = Math.round(duration * 100) / 100
        duration_projected = Math.round(duration_projected * 100) / 100

        header_values.push({column: 'Time Effort ∑', value: time_effort || 0, projected: time_effort_projected || 0, });
        header_values.push({column: 'Manpower ⌀', value: manpower || 0, projected: manpower_projected || 0, });
        header_values.push({column: 'Duration ∑', value: duration || 0, projected: duration_projected || 0, });

        return header_values;
      },

      takt_time_days() {
        let days = [];

        if (this.hasCalendar(this.project_id)) {
          let index = this.$store.state.Ultra.trains[this.train_id].train_start + this.taktarea.taktarea_start + this.taktarea.twaggons.indexOf(parseInt(this.twaggon_id));
          let calendar = this.getCalendar(this.project_id);
          if (index !== -1) {
            calendar.takt_times[index].dates.forEach(date => {
              let name = this.$store.state.Ultra.week_days[date.getDay()];
              let day = {value: name, text: name, takt_date: date.toISOString().substring(0,10)};
              days.push(day);
            });
          }
        }

        return days;
      },

      histprefix(){
        if (this.location === 'HistProjectTaktPlan') {
          return 'hist';
        }
        return '';
      },

      taktarea() {
        return this.$store.state.Ultra[`${this.histprefix}taktareas`][this.taktarea_id];
      },
    },

    methods: {
      ...mapActions('Ultra', [
        'updateUObject',
      ]),

      ...mapActions('SnackBar', [
        'requestSnackbarUpdate'
      ]),

      getWeekDay(date_str) {
        let date = new Date(date_str.split(' UTC')[0]);
        return this.$store.state.Ultra.week_days[date.getDay()];
      },

      updateInputMask(change) {
        let msk = this.input_mask;
        // indices of element array in data
        let time_effort_idx = 0;
        let manpower_idx = 2;
        let duration_idx = 4;

        // when each input was set to something, reset
        if(msk.time_effort != 0 && msk.manpower != 0 && msk.duration != 0) {
          msk.time_effort = 0;
          msk.manpower = 0;
          msk.duration = 0;

          this.on_site_elements[time_effort_idx].value = msk.time_effort;
          this.on_site_elements[manpower_idx].value = msk.manpower;
          this.on_site_elements[duration_idx].value = msk.duration;
        }

        if(change.time_effort) {
          msk.time_effort = change.time_effort;
          this.on_site_elements[time_effort_idx].value = msk.time_effort;
          if(msk.manpower) {
            msk.duration = msk.time_effort / msk.manpower;
            this.on_site_elements[duration_idx].value = msk.duration;
          }
          else if(msk.duration) {
            msk.manpower = msk.time_effort / msk.duration;
            this.on_site_elements[manpower_idx].value = msk.manpower;
          }
        }
        if(change.manpower) {
          msk.manpower = change.manpower;
          this.on_site_elements[manpower_idx].value = msk.manpower;
          if(msk.time_effort) {
            msk.duration = msk.time_effort / msk.manpower;
            this.on_site_elements[duration_idx].value = msk.duration;
          }
          else if(msk.duration) {
            msk.time_effort = msk.duration * msk.manpower;
            this.on_site_elements[time_effort_idx].value = msk.time_effort;
          }
        }
        if(change.duration) {
          msk.duration = change.duration;
          this.on_site_elements[duration_idx].value = msk.duration;
          if(msk.time_effort) {
            msk.manpower = msk.time_effort / msk.duration;
            this.on_site_elements[manpower_idx].value = msk.manpower;
          }
          else if(msk.manpower) {
            msk.time_effort = msk.duration * msk.manpower;
            this.on_site_elements[time_effort_idx].value = msk.time_effort;
          }
        }
      },

      setPlanSource(source) {
        if(this.backlog || this.wallet){
          this.updateUObject({
            url: this.workpackage_url,
            update: {
              time_effort_source: source
            },
          })
        }
        else {
          this.requestSnackbarUpdate({
            url: this.workpackage_url,
            update: {
              time_effort_source: source
            },
          }).then((payload) => {
            this.updateUObject(payload);
          })
        }
      },

      updateTimeEffort(value) {
        if(this.backlog || this.wallet){
          this.updateUObject({
            url: this.workpackage_url,
            update: {
              time_effort_planned: +value
            },
          })
        }
        else {
          this.requestSnackbarUpdate({
            url: this.workpackage_url,
            update: {
              time_effort_planned: +value
            },
          }).then((payload) => {
            this.updateUObject(payload);
          })
        }
      },

      updateManpower(value) {
        if(this.backlog || this.wallet){
          this.updateUObject({
            url: this.workpackage_url,
            update: {
              manpower_planned: value
            },
          })
        }
        else {
          this.requestSnackbarUpdate({
            url: this.workpackage_url,
            update: {
              manpower_planned: value
            },
          }).then((payload) => {
            this.updateUObject(payload);
          })
        }
      },

      // NOTE: this function does not get called on created or mounted as it would fail to find the corresponding dom id
      drawSourceLines() {
        let line_container = document.getElementById('work-package-time-record-svg-container');
        let svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        let { height, width } = line_container.getBoundingClientRect();
        let offset = 2;
        let polyline;
        let points;

        svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
        line_container.replaceChildren(svg);

        // LEFT LINE
        polyline = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
        polyline.setAttribute('fill', 'none');
        polyline.setAttribute('stroke', `${this.workpackage.time_effort_source === 'time_effort_steps'
          ? this.workpackage_fg_hex
          : this.getTranspHexColor(this.workpackage_fg_hex, 0.33)}`
        );
        polyline.setAttribute('stroke-width', this.workpackage.time_effort_source === 'time_effort_steps' ? 2 : 1);

        let left_line = [
          {x: line_container.scrollWidth / 4, y: 0},
          {x: line_container.scrollWidth / 4, y: line_container.scrollHeight / 2 - offset},
          {x: line_container.scrollWidth / 6 - 5 - offset, y: line_container.scrollHeight / 2 - offset},
          {x: line_container.scrollWidth / 6 - 5 - offset, y: line_container.scrollHeight },
        ];
        points = '';

        for(let i = 0; i < left_line.length; i++) {
          points += left_line[i].x; // x-coordinate (left to right)
          points += ',';
          points += left_line[i].y; // y-coordinate (top to bottom)
          points += ' ';
        }

        polyline.setAttribute('points', points);
        svg.appendChild(polyline);

        // RIGHT LINE
        polyline = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
        polyline.setAttribute('fill', 'none');
        polyline.setAttribute('stroke', `${this.workpackage.time_effort_source === 'time_effort_planned'
          ? this.workpackage_fg_hex
          : this.getTranspHexColor(this.workpackage_fg_hex, 0.33)}`
        );
        polyline.setAttribute('stroke-width', this.workpackage.time_effort_source === 'time_effort_planned' ? 2 : 1);

        let right_line = [
          {x: 3 * line_container.scrollWidth / 4, y: 0},
          {x: 3 * line_container.scrollWidth / 4, y: line_container.scrollHeight / 2 + offset},
          {x: line_container.scrollWidth / 6 - 5 + offset, y: line_container.scrollHeight / 2 + offset},
          {x: line_container.scrollWidth / 6 - 5 + offset, y: line_container.scrollHeight },
        ];
        points = '';

        for(let i = 0; i < right_line.length; i++) {
          points += right_line[i].x; // x-coordinate (left to right)
          points += ',';
          points += right_line[i].y; // y-coordinate (top to bottom)
          points += ' ';
        }

        polyline.setAttribute('points', points);
        svg.appendChild(polyline);
      },

      saveTimeRecord() {
        let report = {
          manpower: this.input_mask.manpower,
          duration: this.input_mask.duration,
          date: this.selected_day.takt_date,
        };

        this.createUObject({
          url: [
            {projects: this.project_id},
            {trains: this.train_id},
            {taktareas: this.taktarea_id},
            {twaggons: this.twaggon_id},
            {tworkpackages: this.workpackage_id},
            {dailyreports: ''},
            {showWithEntitiesNormalized: ''}
          ],
          u_object: report
        });
      },

      deleteTimeRecord(id) {
        this.deleteUObject({
          url: [
            {projects: this.project_id},
            {trains: this.train_id},
            {taktareas: this.taktarea_id},
            {twaggons: this.twaggon_id},
            {tworkpackages: this.workpackage_id},
            {dailyreports: id},
          ],
        });
      },

      test(str) {
        if(str)
          console.log(str);
        else
          console.log("test");
      },

      loadData() {

      },
    },
  }
</script>

<style>
.work-package-time-record-container {
  display: flex;
  width: 100%;
}
.work-package-time-record-aggregated-header-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  grid-gap: 4px;
}
.work-package-time-record-aggregated-header-item-column-value {
  display: flex;
  align-items: flex-end;
}
.work-package-time-record-line {
  width: 100%;
  border-bottom: 1px solid;
  opacity: 0.33;
}
.work-package-time-record-input-bottom-outer {
  display: flex;
  width: 100%;
  align-items: flex-end;
  justify-content: space-between;
  padding-bottom: 4px;
}
.work-package-time-record-input-bottom-inner {
  display: flex;
  align-items: flex-start;
  grid-gap: 8px;
}
.work-package-time-record-list-descriptor {
  display: flex;
  flex-direction: column;
}
.work-package-time-record-delete-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.work-package-time-record-plan-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.work-package-time-record-plan-source-container {
  display: flex;
  width: 100%;
  grid-gap: 8px;
}
.work-package-time-record-plan-source-inner-container {
  flex: 1 1 0px;
}
.work-package-time-record-plan-source-header {
  display: flex;
  align-items: center;
  grid-gap: 8px;
}
#work-package-time-record-svg-container {
  width: 100%;
  height: 40px;
}
.work-package-time-record-no-calendar-container {
  display: flex;
  align-items: center;
}
</style>
